import { createContext, use } from "react";
import type { JSX, ReactNode } from "react";
import type { PushNotificationController } from "./pushNotifications";

const PushContext = createContext<PushNotificationController | null>(null);

/** Properties of {@link PushProvider}. */
export interface PushProviderProps {
  readonly controller: PushNotificationController;
  readonly children: ReactNode;
}

/**
 * Provides the push notification controller.
 *
 * @param props - Component properties.
 * @returns The provider element.
 */
export function PushProvider(props: PushProviderProps): JSX.Element {
  const provider = <PushContext value={props.controller}>{props.children}</PushContext>;

  return provider;
}

/**
 * Returns the push notification controller.
 *
 * @returns The controller.
 * @throws Error if no {@link PushProvider} is present.
 */
export function usePushController(): PushNotificationController {
  const controller = use(PushContext);

  if (controller === null) {
    throw new Error("usePushController must be used inside a PushProvider.");
  }

  return controller;
}
